/**
 * AccountMenu — top-right header account control (the firm founder requirement).
 *
 * Replaces the sidebar-bottom `ProfileBubble`. Same body, re-anchored from
 * upward (`bottom-full`) to downward (`top-full mt-2`) and re-homed to the
 * AppShell header's right slot, where every modern app puts the account
 * affordance. Presence of the avatar + name IS the "signed in as X" login
 * indicator (a logged-out user never reaches this shell — `RequireAuth`).
 *
 * Trigger (HoD app-shell-nav-declutter §3.2):
 *   - avatar circle (brand-grad, first initial)
 *   - md+:  avatar + name (truncate, max-w-[140px]) + chevron
 *   - <md:  avatar only (name + chevron hidden — saves header width)
 *
 * Dropdown (§3.3), top→bottom: identity block · Theme toggle · Profile ·
 * Sign out (destructive) · Esc-to-close hint.
 *
 * Behaviors carried verbatim from ProfileBubble: outside-mousedown closes,
 * Esc closes, hover tooltip on the trigger when closed, no sign-out confirm
 * modal (cross-surface canon).
 *
 * Cross-host reuse (P2, SA console consolidation 2026-06-15): the same control
 * powers the AdminShell (super-admin host) top-right. The SA host has no tenant
 * slug — its personal page is `/profile`. The `profileTo` prop overrides the
 * Profile destination (default = the tenant `${base}/profile`).
 * The tenant pill already self-hides when `tenant_slug` is null (SA sessions),
 * so no extra gating is needed there.
 *
 * HoD app-shell-nav-declutter §3 / §6.3.
 * IA cleanup (feat/tenant-profile-ia): Settings link removed from dropdown
 * (left-nav Settings item is the canonical path); Profile re-pointed to the
 * standalone `/:tenantSlug/profile` route (was `/:tenantSlug/settings/profile`).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { Tooltip } from "@/components/common/Tooltip";
import { useLogout } from "@/hooks/queries/auth/useLogout";
import { useMe } from "@/hooks/queries/auth/useMe";
import { useTenantV1Base } from "@/hooks/useTenantV1Base";

function joinName(first: string, last: string): string {
  return `${first ?? ""} ${last ?? ""}`.trim();
}

type AccountMenuProps = {
  /**
   * Profile destination. Defaults to the tenant-scoped `${base}/profile`
   * (the standalone route — NOT nested under /settings).
   * The SA host passes `/profile` (no tenant slug on the admin host).
   */
  profileTo?: string;
};

export function AccountMenu({
  profileTo,
}: AccountMenuProps = {}): JSX.Element {
  // `tenantSlug` is still read for the tenant label below; the link base is
  // prefix-aware so the tenant Profile link stays inside whichever v1 mount the
  // user is on. The SA host passes `profileTo` and never reaches this default.
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  // Called unconditionally (rules of hooks) — `profileTo ?? useTenantV1Base()`
  // would short-circuit the hook away whenever the SA host passes `profileTo`.
  const v1Base = useTenantV1Base();
  const profilePath = profileTo ?? `${v1Base}/profile`;
  const navigate = useNavigate();
  const logout = useLogout();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Esc closes the menu. Matches the `account-menu-kb-hint` footer copy
  // ("Esc to close") — keep the affordance text and the keyboard behavior in
  // lockstep so the hint isn't lying.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const firstName = me.data?.first_name ?? "";
  const lastName = me.data?.last_name ?? "";
  const fullName = joinName(firstName, lastName);
  const email = me.data?.email ?? "";
  const tenantSlugLabel = me.data?.tenant_slug ?? tenantSlug ?? "";
  const displayLabel = fullName || email || "Account";
  const initial = (firstName || fullName || email || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  // Trigger button — shared between tooltip-wrapped (closed) and bare (open)
  // branches. Defined once so the two render paths stay byte-identical.
  // `title` is a non-blocking fallback for non-hover (touch) devices where the
  // Radix tooltip is intentionally suppressed.
  const triggerButton = (
    <button
      type="button"
      data-testid="account-menu-trigger"
      aria-haspopup="menu"
      aria-expanded={open}
      title="Account & sign out"
      onClick={() => setOpen((v) => !v)}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-card-hover"
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-grad text-xs font-semibold text-text-on-brand"
      >
        {initial}
      </span>
      {/* md+: name + chevron. <md: avatar only. */}
      <span className="hidden max-w-[140px] truncate md:inline">
        {displayLabel}
      </span>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        aria-hidden="true"
        className="hidden md:block"
      >
        <path
          d={open ? "M3 7.5l3-3 3 3" : "M3 4.5l3 3 3-3"}
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  return (
    <div className="relative" ref={wrapRef} data-testid="account-menu">
      {open ? (
        triggerButton
      ) : (
        <Tooltip content="Account & sign out" side="bottom">
          {triggerButton}
        </Tooltip>
      )}
      {open ? (
        <div
          role="menu"
          data-testid="account-menu-menu"
          // `z-50`: the AppShell header band that hosts this control does NOT
          // establish its own stacking context (no z-index/transform/isolate/
          // opacity), so this dropdown shares the root stacking context with the
          // page surface. Page content like the Hiring-Intelligence KPI grid is
          // `relative z-10`, which would otherwise paint OVER an unstacked
          // (z-auto) dropdown and obscure its lower rows. `z-50` (the app's
          // overlay tier — drawer/scrim/tooltip all use it) lifts the dropdown
          // above any `relative z-10` page content. No `overflow`/`transform`
          // ancestor traps it, so a z-index escapes cleanly — no portal needed.
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-border-default bg-surface-card shadow-md"
        >
          {/* Identity block — non-clickable header. 3 rows: name / email /
              tenant pill. Verbatim from ProfileBubble. */}
          <div className="flex flex-col gap-0.5 border-b border-border-default px-4 py-3">
            {fullName ? (
              <span className="truncate text-sm font-medium text-text-primary">
                {fullName}
              </span>
            ) : null}
            <span className="truncate text-xs text-text-secondary">{email}</span>
            {tenantSlugLabel ? (
              <span className="mt-1 truncate text-[10px] font-bold uppercase tracking-[0.16em] text-text-tertiary">
                {tenantSlugLabel}
              </span>
            ) : null}
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm text-text-secondary">
            <span>Theme</span>
            <ThemeToggle />
          </div>
          <button
            role="menuitem"
            type="button"
            data-testid="account-menu-profile-link"
            onClick={() => {
              setOpen(false);
              navigate(profilePath);
            }}
            className="block w-full px-4 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-surface-card-hover hover:text-text-primary"
          >
            Profile
          </button>
          {/* Sign out — destructive treatment. `text-status-rejected-fg`
              + `border-t` separator above. No confirmation modal (cross-surface
              canon). Verbatim from ProfileBubble. */}
          <button
            role="menuitem"
            type="button"
            data-testid="account-menu-signout"
            onClick={() => {
              setOpen(false);
              logout.mutate();
            }}
            className="block w-full border-t border-border-default px-4 py-2 text-left text-sm font-medium text-status-rejected-fg transition-colors hover:bg-surface-card-hover"
          >
            Sign out
          </button>
          {/* Keyboard-hint footer row — non-interactive. Wired to the keydown
              handler above. */}
          <div
            role="presentation"
            data-testid="account-menu-kb-hint"
            className="border-t border-border-default px-4 py-2 text-[10px] text-text-tertiary"
          >
            Esc to close
          </div>
        </div>
      ) : null}
    </div>
  );
}
